import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { FEATURE_FLAGS_CONFIG_TOKEN, FeatureFlagsConfig, DEFAULT_LIB_CONFIG } from './feature-flags.config';

@Injectable({ providedIn: 'root' })
/**
 * Service to manage feature flags and provide a unified configuration.
 * Will be used to show / hide specific UI elements based on feature flags.
 * For now it's local, but will be extended to support remote config loading - when BE implements it
 */
export class FeatureFlagsService {
  private readonly http = inject(HttpClient);
  // Inject the override from the App (or an empty object if none provided)
  private readonly _userOverrides = inject(FEATURE_FLAGS_CONFIG_TOKEN, { optional: true }) ?? {};

// 1. Initialize the signal with merged defaults + local overrides
  private readonly _config = signal<FeatureFlagsConfig>(
    this.deepMerge(DEFAULT_LIB_CONFIG, this._userOverrides)
  );

  // 2. Expose as a read-only signal for components
  readonly config = this._config.asReadonly();

  /**
   * Future-proof: Call this to update flags from an API
   */
  async loadRemoteConfig(apiUrl: string): Promise<void> {
    try {
      const remoteConfig = await firstValueFrom(this.http.get<Partial<FeatureFlagsConfig>>(apiUrl));

      // Update the signal. Signals will notify all templates automatically.
      this._config.update(current => this.deepMerge(current, remoteConfig));
    } catch (error) {
      console.error('Remote config failed, staying with current values.', error);
    }
  }

  private deepMerge(target: any, source: any): any {
    const output = { ...target };

    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          // If the key is an object, merge recursively
          output[key] = this.deepMerge(target[key] || {}, source[key]);
        } else {
          // If it's a primitive value, just overwrite
          output[key] = source[key];
        }
      });
    }
    return output;
  }

  private isObject(item: any): boolean {
    return (item && typeof item === 'object' && !Array.isArray(item));
  }
}
